// Human labels for bulk-activity failure / skip reason codes. Shared by the
// recent-activities page (ActivityItem) and the global bulk-operations queue
// widget (StatusIndicator) so both surfaces show the same wording for a reason.
//
// Reason codes come from the backend adapters' track_skip/track_failure calls
// (e.g. flow remove -> "already_sent", matching the synchronous destroy API).
export const FAILED_REASON_LABELS: Record<string, string> = {
  // Add to job
  err_already_exist: 'Added to job already',
  err_already_in_job: 'Added to job already',
  err_application_exists: 'Added to job already', // add_to_job adapter skip reason (candidate already in the job)
  // Add to flow
  err_another_active_outreach: 'Active in another flow',
  err_already_present_outreach: 'Added to flow already',
  err_already_exists: 'Added to flow already',
  // Add to project
  err_already_prospect: 'Added to project already',
  // Bulk reject, bulk email, Hireflix, self-schedule
  err_email_missing: 'Missing email',
  err_invalid_email: 'Invalid email',
  // Bulk reject
  err_already_rejected: 'Rejected already',
  err_job_reopened: 'Job reopened',
  // Hireflix
  err_received_assessment_already: 'Received assessment already',
  err_already_received_assessment: 'Received assessment already',
  // Bulk self-schedule
  err_in_this_interview_already: 'In this interview already',
  err_already_in_interview: 'In this interview already',
  err_sender_mailbox_not_connected: 'Sender mailbox not connected',
  // AI scoring
  err_scoring_failed: 'Failed to update AI score',
  // Send for review (project_send_review)
  err_already_in_review: 'Already in review',
  err_review_failed: 'Failed to send for review',
  // Remove from project (remove_prospect)
  err_not_a_prospect: 'Not in this project',
  // Flow bulk actions (pause/resume/stop/move-step/remove/move-to-flow)
  already_sent: 'Message already sent, so not removable', // remove API reason: a step was already sent, so the candidate can't be removed
  err_not_in_flow: 'Not in this flow',
  err_invalid_step_movement: 'Invalid step movement',
  err_move_to_flow_failed: 'Could not move to flow',
  err_kula_ats_application_for_job: 'Already in a Kula job',
  err_ats_application_active_for_job: 'Active application in job',
  // Fallbacks
  err_internal: 'Internal error',
};

/**
 * Format a failure reason code into a readable label. Prefers an explicit
 * backend message, then the curated map, then a title-cased fallback.
 */
export const formatFailedReason = (code: string, message?: string): string => {
  if (message?.trim()) return message;
  if (FAILED_REASON_LABELS[code]) return FAILED_REASON_LABELS[code];
  return code
    .replace(/^err_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * Pick the highest-count failure reason from an activity's `{ code: count }`
 * error map and return its human label — used for the queue widget's compact
 * error badge (which only has room for a single reason).
 */
export const dominantFailedReason = (
  errors?: Record<string, number>
): string | undefined => {
  const codes = Object.keys(errors ?? {});
  if (!codes.length) return undefined;
  const dominant = codes.reduce((a, b) =>
    (errors![b] ?? 0) > (errors![a] ?? 0) ? b : a
  );
  return formatFailedReason(dominant);
};
